import { useState } from "react";
import {
  Loader2,
  AlertCircle,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useFiscalYears } from "@/features/fiscal-calendar";
import {
  useBalanceSheet,
  type BalanceSheetItem,
  type BalanceSheetSection,
} from "@/features/reports";

/**
 * Format a date string for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Format a number as currency.
 */
function formatCurrency(value: string): string {
  const num = parseFloat(value);
  return num.toLocaleString("es-CL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Balance Sheet section component.
 */
function BalanceSheetSectionView({
  section,
  netIncome,
}: {
  section: BalanceSheetSection;
  netIncome?: { label: string; amount: string };
}) {
  const [expandedSubtypes, setExpandedSubtypes] = useState<Set<string>>(
    new Set(section.subtotals.map((s) => s.subtype)),
  );

  const toggleSubtype = (subtype: string) => {
    const newExpanded = new Set(expandedSubtypes);
    if (newExpanded.has(subtype)) {
      newExpanded.delete(subtype);
    } else {
      newExpanded.add(subtype);
    }
    setExpandedSubtypes(newExpanded);
  };

  // Group items by subtype
  const itemsBySubtype: Record<string, BalanceSheetItem[]> = {};
  for (const item of section.items) {
    const subtype = item.accountSubtype ?? "OTHER";
    if (!itemsBySubtype[subtype]) {
      itemsBySubtype[subtype] = [];
    }
    itemsBySubtype[subtype].push(item);
  }

  return (
    <div className="space-y-1">
      {/* Section header */}
      <div className="bg-primary/10 px-4 py-2 font-semibold text-primary">
        {section.label}
      </div>

      {/* Subtypes with items */}
      {section.subtotals.map((subtotal) => {
        const items = itemsBySubtype[subtotal.subtype] ?? [];
        const isExpanded = expandedSubtypes.has(subtotal.subtype);

        return (
          <div key={subtotal.subtype} className="border-b last:border-0">
            {/* Subtype header */}
            <button
              className="w-full flex items-center justify-between px-4 py-2 hover:bg-muted/50 transition-colors"
              onClick={() => toggleSubtype(subtotal.subtype)}
            >
              <div className="flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                <span className="font-medium">{subtotal.label}</span>
              </div>
              <span className="font-mono">
                ${formatCurrency(subtotal.total)}
              </span>
            </button>

            {/* Items (when expanded) */}
            {isExpanded && items.length > 0 && (
              <div className="bg-muted/20">
                {items.map((item) => (
                  <div
                    key={item.accountId}
                    className={cn(
                      "flex items-center justify-between px-4 py-1.5 text-sm",
                      item.isHeader && "font-medium text-muted-foreground",
                    )}
                    style={{ paddingLeft: `${(item.level + 2) * 16}px` }}
                  >
                    <span>
                      {item.accountCode} - {item.accountName}
                    </span>
                    {!item.isHeader && (
                      <span className="font-mono">
                        {parseFloat(item.balance) < 0 ? "(" : ""}$
                        {formatCurrency(
                          Math.abs(parseFloat(item.balance)).toFixed(2),
                        )}
                        {parseFloat(item.balance) < 0 ? ")" : ""}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Net Income (for Equity section only) */}
      {netIncome && (
        <div className="flex items-center justify-between px-4 py-2 bg-muted/30 border-t">
          <span className="font-medium pl-6">{netIncome.label}</span>
          <span className="font-mono">
            {parseFloat(netIncome.amount) < 0 ? "(" : ""}$
            {formatCurrency(Math.abs(parseFloat(netIncome.amount)).toFixed(2))}
            {parseFloat(netIncome.amount) < 0 ? ")" : ""}
          </span>
        </div>
      )}

      {/* Section total */}
      <div className="flex items-center justify-between px-4 py-2 bg-primary/5 font-semibold">
        <span>Total {section.label}</span>
        <span className="font-mono">${formatCurrency(section.total)}</span>
      </div>
    </div>
  );
}

/**
 * Balance General (Balance Sheet) page.
 */
export function BalanceGeneralPage() {
  // Fiscal year selector state
  const [selectedFiscalYearId, setSelectedFiscalYearId] = useState<string>("");
  const [asOfDate, setAsOfDate] = useState<string>("");
  const [includeZeroBalances, setIncludeZeroBalances] = useState(false);

  // Fetch fiscal years for selector
  const { years: fiscalYears, isLoading: fiscalYearsLoading } =
    useFiscalYears();

  // Fetch balance sheet
  const {
    report,
    isLoading: reportLoading,
    error: reportError,
  } = useBalanceSheet({
    fiscalYearId: selectedFiscalYearId || undefined,
    asOfDate: asOfDate || undefined,
    includeZeroBalances,
  });

  // Get selected fiscal year for date validation
  const selectedFiscalYear = fiscalYears.find(
    (fy) => fy.id === selectedFiscalYearId,
  );

  // Export to Excel placeholder
  const handleExport = () => {
    alert("Exportar a Excel - Proximamente");
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Balance General</h1>
          <p className="text-sm text-muted-foreground">
            Estado de situacion financiera
          </p>
        </div>
        {report && (
          <Button variant="outline" onClick={handleExport}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Exportar
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        {/* Fiscal year selector */}
        <div className="w-[200px]">
          <label className="text-sm font-medium mb-1 block">Ano Fiscal</label>
          <Select
            value={selectedFiscalYearId}
            onValueChange={(value) => {
              setSelectedFiscalYearId(value);
              // Reset asOfDate when changing fiscal year
              setAsOfDate("");
            }}
            disabled={fiscalYearsLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar..." />
            </SelectTrigger>
            <SelectContent>
              {fiscalYears.map((fy) => (
                <SelectItem key={fy.id} value={fy.id}>
                  {fy.yearNumber}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* As of date */}
        <div>
          <label className="text-sm font-medium mb-1 block">A la Fecha</label>
          <Input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            min={selectedFiscalYear?.startDate}
            max={selectedFiscalYear?.endDate}
            disabled={!selectedFiscalYearId}
            className="w-[160px]"
          />
          {selectedFiscalYear && !asOfDate && (
            <p className="text-xs text-muted-foreground mt-1">
              Por defecto: {formatDate(selectedFiscalYear.endDate)}
            </p>
          )}
        </div>

        {/* Include zero balances */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="includeZeroBalances"
            checked={includeZeroBalances}
            onChange={(e) => setIncludeZeroBalances(e.target.checked)}
            className="rounded border-gray-300"
          />
          <label htmlFor="includeZeroBalances" className="text-sm">
            Incluir cuentas sin saldo
          </label>
        </div>
      </div>

      {/* Content */}
      {!selectedFiscalYearId ? (
        // No fiscal year selected
        <div className="border rounded-lg p-12 bg-card flex flex-col items-center justify-center text-center">
          <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Seleccione un ano fiscal</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Elija un ano fiscal para generar el Balance General a la fecha de
            cierre o a una fecha especifica dentro del periodo.
          </p>
        </div>
      ) : reportLoading ? (
        // Loading state
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : reportError ? (
        // Error state
        <div className="flex flex-col items-center justify-center h-64 gap-2 text-destructive">
          <AlertCircle className="h-8 w-8" />
          <p>Error al generar el reporte</p>
          <p className="text-sm text-muted-foreground">{reportError.message}</p>
        </div>
      ) : report ? (
        // Balance Sheet report
        <div className="space-y-4">
          {/* Report header */}
          <div className="border rounded-lg bg-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">
                  {report.organizationName}
                </h2>
                <p className="text-sm text-muted-foreground">
                  Balance General al {formatDate(report.asOfDate)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Ano Fiscal {report.fiscalYear.yearNumber} | Moneda:{" "}
                  {report.currency}
                </p>
              </div>
              <div
                className={cn(
                  "flex items-center gap-2 px-3 py-1 rounded-full text-sm",
                  report.isBalanced
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800",
                )}
              >
                {report.isBalanced ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Cuadrado
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    Descuadrado
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Balance Sheet sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Assets */}
            <div className="border rounded-lg bg-card overflow-hidden">
              <BalanceSheetSectionView section={report.assets} />
            </div>

            {/* Liabilities + Equity */}
            <div className="space-y-4">
              {/* Liabilities */}
              <div className="border rounded-lg bg-card overflow-hidden">
                <BalanceSheetSectionView section={report.liabilities} />
              </div>

              {/* Equity */}
              <div className="border rounded-lg bg-card overflow-hidden">
                <BalanceSheetSectionView
                  section={report.equity}
                  netIncome={report.equity.netIncome}
                />
              </div>
            </div>
          </div>

          {/* Summary totals */}
          <div className="border rounded-lg bg-card p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-primary/5 rounded">
                <span className="font-semibold">Total Activos</span>
                <span className="font-mono text-lg font-semibold">
                  ${formatCurrency(report.totalAssets)}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-primary/5 rounded">
                <span className="font-semibold">
                  Total Pasivos + Patrimonio
                </span>
                <span className="font-mono text-lg font-semibold">
                  ${formatCurrency(report.totalLiabilitiesAndEquity)}
                </span>
              </div>
            </div>
            {!report.isBalanced && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                <strong>Advertencia:</strong> El balance no cuadra. Diferencia:{" "}
                $
                {formatCurrency(
                  (
                    parseFloat(report.totalAssets) -
                    parseFloat(report.totalLiabilitiesAndEquity)
                  ).toFixed(2),
                )}
              </div>
            )}
          </div>

          {/* Generated timestamp */}
          <p className="text-xs text-muted-foreground text-center">
            Generado el {new Date(report.generatedAt).toLocaleString("es-CL")}
          </p>
        </div>
      ) : null}
    </div>
  );
}
